import { getAgencyApiHost } from 'api/host-by-env';
import { handleError } from 'utilities/utilities';

export interface PayOrdersWithRangePayload {
  firmId: string | number;
  fromDate: string;
  toDate: string;
}

export interface PayOrderModel {
  isReopened: boolean;
  isOrderAutoReport: boolean;
  fileOrderIds?: number[];
  fileNumbers?: string[];
  sharedFileNumbers?: string[];
  policyNumbers?: string[];
  orderNumber: string;
  orderDate: string;
  orderStatus: string;
  invoiceNumber?: string;
  invoiceType: string;
  underwriter: string;
  gross: number;
  net: number;
  paymentsCredits: number;
  amountDue: number;
  batchId?: number;
  paymentId?: number;
}

function mapPayOrder(item: Record<string, unknown>): PayOrderModel {
  return {
    isReopened: (item.IsReopenedForRemit as boolean) || false,
    isOrderAutoReport: (item.IsOrderAutoReport as boolean) || false,
    fileOrderIds: item.FileOrderIds as number[] | undefined,
    fileNumbers: item.FileNumbers as string[] | undefined,
    sharedFileNumbers: item.SharedFileNumbers as string[] | undefined,
    policyNumbers: item.PolicyNumbers as string[] | undefined,
    orderNumber: item.OrderNumber as string,
    orderDate: item.OrderDate as string,
    orderStatus: item.OrderStatus as string,
    invoiceNumber: item.InvoiceNumber as string | undefined,
    invoiceType: item.InvoiceType as string,
    underwriter: item.Underwriter as string,
    gross: (item.Gross as number) || 0,
    net: (item.Net as number) || 0,
    paymentsCredits: (item.PaymentsCredits as number) || 0,
    amountDue: (item.AmountDue as number) || 0,
    batchId: item.BatchId as number | undefined,
    paymentId: item.PaymentId as number | undefined,
  };
}

/**
 * Fetch pay orders with date range directly from Agency API.
 * Bypasses RemittanceService Lambda to avoid 30-second timeout.
 */
export async function getPayOrdersWithRangeDirect(
  token: string,
  payload: PayOrdersWithRangePayload,
): Promise<PayOrderModel[]> {
  const firmId = typeof payload.firmId === 'string' ? parseInt(payload.firmId, 10) : payload.firmId;
  const params = new URLSearchParams({
    firmId: String(firmId),
    fromDate: payload.fromDate,
    toDate: payload.toDate,
  });
  const url = `${getAgencyApiHost()}remittance-service/v1/payorders?${params.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }).catch((error) => {
    throw Error(
      `Error occurred while fetching pay orders with date range. firmId: ${firmId}, fromDate: ${
        payload.fromDate
      }, toDate: ${payload.toDate}, error: ${JSON.stringify(error)}`,
    );
  });

  const json = await response.json();
  handleError(json);

  const result = json.Result ?? json.data?.Result ?? [];
  return (result as Record<string, unknown>[]).map(mapPayOrder);
}

/**
 * Fetch payment transmittal sheet PDF directly from Agency API.
 * Bypasses RemittanceService Lambda to avoid 28-second timeout.
 *
 * @param token - MSAL Bearer token
 * @param paymentId - Payment ID
 * @returns Promise with transmittalSheet (base64-encoded PDF), matching Portal API response shape
 */
export async function getTransmittalSheetDirect(
  token: string,
  paymentId: number,
): Promise<{ transmittalSheet: string }> {
  const params = new URLSearchParams({ paymentId: String(paymentId) });
  const url = `${getAgencyApiHost()}remittance-service/v1/transmittal-sheet?${params.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }).catch((error) => {
    throw Error(
      `Error occurred while fetching transmittal sheet. paymentId: ${paymentId}, error: ${JSON.stringify(error)}`,
    );
  });

  const json = await response.json();
  handleError(json);

  return {
    transmittalSheet: json.Result,
  };
}

export interface ExportPayOrdersRequest {
  FirmId: number; // Agency API expects number, not string
  Orders: Array<{
    orderNumber: string;
    invoiceType: string;
  }>;
}

export interface ExportPayOrdersResponse {
  Result: string; // base64 Excel data
}

/**
 * Export pay orders directly from Agency API
 * Bypasses RemittanceService Lambda to avoid 28-second timeout
 *
 * @param token - MSAL Bearer token
 * @param firmId - Firm ID as string (will be converted to number)
 * @param orders - Array of orders to export
 * @returns Promise with excelData (base64-encoded Excel file)
 *
 * @example
 * const result = await exportPayOrdersDirect(token, "12345", [
 *   { orderNumber: "123456", invoiceType: "Remittance" }
 * ]);
 * // result: { excelData: "base64..." }
 */
export async function exportPayOrdersDirect(
  token: string,
  firmId: string,
  orders: Array<{ orderNumber: string; invoiceType: string }>,
): Promise<{ excelData: string }> {
  const url = `${getAgencyApiHost()}remittance-service/v1/payorders/export`;

  const requestBody: ExportPayOrdersRequest = {
    FirmId: parseInt(firmId, 10), // Convert string to number for Agency API
    Orders: orders,
  };

  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(requestBody),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  }).catch((error) => {
    throw Error(
      `Error occurred while exporting pay orders. firmId: ${firmId}, orders count: ${
        orders.length
      }, error: ${JSON.stringify(error)}`,
    );
  });

  const json = await response.json();

  // handleError checks for data.Errors array (Agency API format)
  handleError(json);

  // Transform response: { data: { Result: "..." } } → { excelData: "..." }
  // Agency API may return Result at top level or nested in data
  return {
    excelData: json.data?.Result || json.Result,
  };
}
